import { ReactNode, CSSProperties, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import LoadingButton from './LoadingButton';

//-----------------------------------------------------------------------------------

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  text?: string;
  agreeColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | any;
  disagreeColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | any;
  agreeText?: string;
  disagreeText?: string;
  onAgree?: (_callback: any) => void;
  onDisagree?: () => void;
  disagreeFocus?: boolean;
  agreeFocus?: boolean;
  buttonVariant?: 'text' | 'contained' | 'outlined' | any;
  children?: ReactNode;
  sx?: CSSProperties;
}

export default function ConfirmationDialog({
  open,
  onClose,
  title,
  text,
  agreeColor = 'primary',
  disagreeColor = 'greyTheme',
  agreeText,
  disagreeText,
  onAgree,
  onDisagree,
  disagreeFocus,
  agreeFocus,
  buttonVariant = 'text',
  children,
  sx,
}: ConfirmationDialogProps) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const handleAgree = () => {
    setLoading(true);
    setTimeout(async () => {
      if (onAgree) {
        await onAgree(() => {});
      }
      setLoading(false);
      onClose();
    }, 0);
  };

  const handleDisagree = () => {
    onClose();
    if (onDisagree) {
      onDisagree();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      sx={sx}
      maxWidth={'xs'}
      fullWidth
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {open ? (
        <>
          <DialogTitle id="alert-dialog-title">{title || t('dialog.confirmation.title')}</DialogTitle>
          <DialogContent>
            {children}
            {text && <DialogContentText id="alert-dialog-description">{text}</DialogContentText>}
          </DialogContent>
          <DialogActions>
            <LoadingButton
              id={'confirmationDialog-disagreeButton'}
              variant={buttonVariant}
              color={disagreeColor}
              onClick={handleDisagree}
              autoFocus={disagreeFocus}
              disabled={loading}
              loading={loading}
            >
              {disagreeText || t('dialog.confirmation.disagree')}
            </LoadingButton>
            <LoadingButton
              id={'confirmationDialog-agreeButton'}
              variant={buttonVariant}
              color={agreeColor}
              onClick={handleAgree}
              autoFocus={agreeFocus || !disagreeFocus}
              disabled={loading}
              loading={loading}
            >
              {agreeText || t('dialog.confirmation.agree')}
            </LoadingButton>
          </DialogActions>
        </>
      ) : (
        <Box sx={{ width: '100%', height: '100%' }} display={'flex'} alignItems={'center'} justifyContent={'center'}>
          <CircularProgress disableShrink />
        </Box>
      )}
    </Dialog>
  );
}
